import { Component, ComponentType } from 'react'
import * as omit from 'lodash/omit'
import * as debounce from 'lodash/debounce'
import { FlagsProps, LoadersProps } from 'interfaces/guards'
import { checkGuards, checkIfLoaded } from 'helpers/loadableHelpers'
import { GUARD_LOADING_TIMEOUT } from 'constants/component'
import * as styles from 'styles/ShadedLoadable.css'
import * as cn from 'classnames'
import Spinner from 'components/features/pfm/Spinner/Spinner'

type StaleableLoadableStateProps = FlagsProps & LoadersProps

type StaleableLoadableComponentsProps<TComponentsProps> = {
  content: ComponentType<TComponentsProps>
  preloader: ComponentType<TComponentsProps>
  isLoaded: boolean
  hasData: boolean
}

export type StaleableLoadableOwnProps<TComponentsProps> =
  & TComponentsProps
  & StaleableLoadableComponentsProps<TComponentsProps>

export type StaleableLoadableProps<TComponentsProps> =
  & StaleableLoadableStateProps
  & StaleableLoadableOwnProps<TComponentsProps>

export const OMITABLE_PROP_NAMES: (keyof StaleableLoadableStateProps | keyof StaleableLoadableComponentsProps<any>)[] = [
  'flags', 'loaders', 'content', 'preloader', 'loaderNames',
]

class StaleableLoadable<TComponentsProps extends {}> extends Component<StaleableLoadableProps<TComponentsProps>> {
  render () {
    // It is TS bug, I should declare types explicitly
    const Content: ComponentType<TComponentsProps> = this.props.content
    const { isLoaded, hasData } = this.props
    const props = this.getComponentsProps(this.props)

    return (
      <div className={ cn(styles.shadedLoadable, { [styles.isShown]: !isLoaded }) }>
        { hasData && <Content { ...props } /> }
        { !hasData ? this.renderPreloader() : this.renderSpinner() }
      </div>
    )
  }

  renderSpinner () {
    if (this.props.isLoaded) {
      return null
    }

    return (
      <div className={ styles.overlay }>
        <Spinner />
      </div>
    )
  }

  renderPreloader () {
    const Preloader: ComponentType<{}> = this.props.preloader

    return (
      <Preloader />
    )
  }

  componentDidMount () {
    const { flags, loaders } = this.props
    checkGuards(flags, loaders)

    const { isLoaded } = this.props
    if (isLoaded) {
      this.setState({ hasEverBeenLoaded: true })
    } else {
      this.startLoadingChecking()
    }
  }

  componentDidUpdate () {
    const { isLoaded, flags, loaders } = this.props
    if (!isLoaded) {
      checkGuards(flags, loaders)
    }

    // restart checking each time
    this.startLoadingChecking.cancel()

    if (!isLoaded) {
      this.startLoadingChecking()
    }
  }

  componentWillUnmount () {
    this.startLoadingChecking.cancel()
  }

  private checkIfLoaded = () => {
    const { isLoaded, flags, loaderNames, content } = this.props
    return checkIfLoaded(isLoaded, flags, loaderNames, content)
  }

  private startLoadingChecking = debounce(this.checkIfLoaded, GUARD_LOADING_TIMEOUT)

  private getComponentsProps (props: Readonly<StaleableLoadableProps<TComponentsProps>>): Readonly<TComponentsProps> {
    return omit(props, OMITABLE_PROP_NAMES) as TComponentsProps
  }
}


export default StaleableLoadable
